/**
 * API client.
 *
 * Request/response TYPES are generated from the backend's OpenAPI schema and
 * derived here as aliases over `./schema.gen` (`components["schemas"][...]`),
 * so they can't drift from the backend contract — the CI contract-drift gate
 * enforces it (CHP-1111 / ADR-0049). Regenerate with `npm run gen:api` after
 * `python apps/api/scripts/dump_openapi.py`.
 *
 * The FUNCTIONS stay hand-authored: the bearer-token wiring, `ApiError`
 * normalisation, and the multipart / presigned-URL / blob flows aren't
 * expressible from OpenAPI (a deliberate end state — see ADR-0049). A few types
 * with no named backend schema (inlined enums, union responses, query-param
 * bundles) also stay hand-authored below.
 */

import type { components } from "./schema.gen";

const baseUrl = import.meta.env.VITE_API_BASE_URL;

// Slice 4c: "pending" is retained on the wire as a legacy enum value used
// by `role_from_groups` and the Authentik group mapping, but the local
// users.role column no longer stores it — pending users have role=NULL
// + status='pending'. `CurrentUser.role` and `AdminUserListItem.role`
// widen to Role | null accordingly.
export type Role = components["schemas"]["Role"];

// Slice 4c: lifecycle status, orthogonal to role. Pending users have
// role=null + status='pending'. Deactivated rows have is_active=false +
// status='deactivated' (the columns stay in lockstep — admin PATCH does
// this). Slice 0018: 'closed' is the terminal state after DELETE
// /admin/users/{id}; email + authentik_sub are released.
export type Status = "pending" | "active" | "rejected" | "deactivated" | "closed";

export type PartnerTier = components["schemas"]["PartnerTier"];
export type Region = components["schemas"]["Region"];
export type SpifPayoutType = components["schemas"]["SpifPayoutType"];

export type Organization = components["schemas"]["OrganizationOut"];

export type CurrentUser = components["schemas"]["CurrentUserOut"];

export type AssignedChannelManager = components["schemas"]["AssignedChannelManagerOut"];

export type Profile = components["schemas"]["ProfileOut"];

export type ProfileUpdatePayload = components["schemas"]["ProfileUpdate"];

// Account Creation slice 1: pre-T&C onboarding wizard. Subset of
// ProfileUpdatePayload — only the fields the wizard captures. The endpoint
// is POST /auth/onboarding and is gated on require_active_user only.
export type OnboardingUpdatePayload = components["schemas"]["OnboardingUpdate"];

// Account Creation slice 3: PO bulk-invites BDMs under their own org.
// POST /partners/me/team-invites returns a per-row outcome so the UI
// can render success/failure inline rather than re-submitting on
// partial failure. `reason` is a short stable code (`duplicate` |
// `authentik_error` | `unexpected`) — the UI maps it to a friendly
// message, doesn't pattern-match `detail`.
// CHP-295: per-invitee partner role — "po" mints a peer Program Owner
// (full org access), "bdm" the subordinate default.
export type TeamInviteRole = "bdm" | "po";

export type TeamInviteItem = components["schemas"]["TeamInviteItem"];

export type TeamInvitePayload = components["schemas"]["TeamInviteRequest"];

export type TeamInviteSuccess = components["schemas"]["TeamInviteSuccess"];

export type TeamInviteFailure = components["schemas"]["TeamInviteFailure"];

export type TeamInviteResponse = components["schemas"]["TeamInviteResponse"];

export type AdminInvitePayload = components["schemas"]["AdminInviteRequest"];

export type AdminInviteResponse = components["schemas"]["AdminInviteResponse"];

export type AdminUserListItem = components["schemas"]["AdminUserListItemOut"];

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {},
): Promise<T> {
  const url = `${baseUrl}${path}`;
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const resp = await fetch(url, { ...init, headers });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export function fetchMe(token: string): Promise<CurrentUser> {
  return request("/auth/me", token);
}

export function fetchMyProfile(token: string): Promise<Profile | null> {
  return request("/profiles/me", token);
}

export function upsertMyProfile(token: string, payload: ProfileUpdatePayload): Promise<Profile> {
  return request("/profiles/me", token, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Settings → Notifications (email-only on/off preferences)
// ---------------------------------------------------------------------------
// Three email-only toggles per the source spec (Confluence 701104186 — "Select
// the updates you would like to receive via email."). Every user is opted in by
// default (server_default true).
export type NotificationPrefs = components["schemas"]["NotificationPrefsRead"];

// Partial update — any subset of the categories. extra="forbid" on the backend,
// so only these keys are accepted.
export type NotificationPrefsUpdatePayload = components["schemas"]["NotificationPrefsUpdate"];

export function fetchNotificationPrefs(token: string): Promise<NotificationPrefs> {
  return request("/settings/notifications", token);
}

export function updateNotificationPrefs(
  token: string,
  payload: NotificationPrefsUpdatePayload,
): Promise<NotificationPrefs> {
  return request("/settings/notifications", token, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listProfiles(token: string, params: URLSearchParams = new URLSearchParams()) {
  const qs = params.toString();
  return request<Profile[]>(`/profiles/${qs ? `?${qs}` : ""}`, token);
}

export function fetchProfile(token: string, userId: string): Promise<Profile> {
  return request(`/profiles/${userId}`, token);
}

export function listAdminUsers(token: string, params: URLSearchParams = new URLSearchParams()) {
  const qs = params.toString();
  return request<AdminUserListItem[]>(`/admin/users${qs ? `?${qs}` : ""}`, token);
}

/** One append-only audit_log row (admin-gated GET /admin/audit, CHP-335). */
export type AuditLogEntry = components["schemas"]["AuditLogOut"];

/** Newest-first audit entries; supports `limit` (≤500) + `offset` params. */
export function listAuditLog(token: string, params: URLSearchParams = new URLSearchParams()) {
  const qs = params.toString();
  return request<AuditLogEntry[]>(`/admin/audit${qs ? `?${qs}` : ""}`, token);
}

// M3: per-role counts, used by the admin dashboard's stat-card row.
// CHP — Admin Hub redesign: the endpoint now also returns active-only
// sub-counts that drive the two spec stat cards ("Total Active Partners"
// with a PO/BDM split, "Total Admin Users" with a CM/RA split). The legacy
// per-role keys (closed-excluded, all statuses) stay for back-compat.
export type UserCount = Record<Role, number> & {
  /** Active channel partners (PO + BDM). */
  channel_partner_total: number;
  /** Active POs (PartnerProfile.is_org_admin = true). */
  channel_partner_po: number;
  /** Active BDMs (PartnerProfile.is_org_admin = false). */
  channel_partner_bdm: number;
  /** Active channel managers. */
  channel_manager_count: number;
  /** Active ResetData Masters (role=admin, is_resetdata_master=true). */
  resetdata_master_count: number;
  /** Active ResetData Admins (role=admin, is_resetdata_master=false/null). */
  resetdata_admin_count: number;
  /** Internal admin-tier headcount: CM + RM + RA. Card 2's primary number. */
  admin_total: number;
};

export function fetchUserCount(token: string): Promise<UserCount> {
  return request<UserCount>("/admin/users/_count", token);
}

export type AdminUserPatch = components["schemas"]["UserAdminUpdate"];

export function patchAdminUser(token: string, userId: string, payload: AdminUserPatch) {
  return request<AdminUserListItem>(`/admin/users/${userId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Slice 0018: user lifecycle (deactivate / reactivate / delete)
// ---------------------------------------------------------------------------

export type DeactivationReason =
  "staff_turnover" | "partner_offboarding" | "compliance" | "voluntary" | "other";

export const DEACTIVATION_REASON_LABEL: Record<DeactivationReason, string> = {
  staff_turnover: "Staff turnover",
  partner_offboarding: "Partner offboarding",
  compliance: "Compliance",
  voluntary: "Voluntary / self-removal",
  other: "Other",
};

export type UserDeactivatePayload = components["schemas"]["UserDeactivatePayload"];

export type OpenWorkDealItem = components["schemas"]["OpenWorkDealItem"];

export type OpenWorkSpifItem = components["schemas"]["OpenWorkSpifItem"];

export type OpenWorkSummary = components["schemas"]["OpenWorkSummary"];

export type OpenWork = components["schemas"]["OpenWorkPreview"];

/** Body shape of the 409 returned by deactivate when open work blocks it. */
export interface OpenWorkBlockedDetail {
  code: "open_work";
  message: string;
  open_work: OpenWork;
}

export function getUserOpenWork(token: string, userId: string): Promise<OpenWork> {
  return request<OpenWork>(`/admin/users/${userId}/open-work`, token);
}

export function deactivateUser(
  token: string,
  userId: string,
  payload: UserDeactivatePayload,
): Promise<AdminUserListItem> {
  return request<AdminUserListItem>(`/admin/users/${userId}/deactivate`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function reactivateUser(token: string, userId: string): Promise<AdminUserListItem> {
  return request<AdminUserListItem>(`/admin/users/${userId}/reactivate`, token, {
    method: "POST",
  });
}

// CHP-322: replace a Channel Manager's / ResetData Admin's permission toggles
// after invite. Returns the updated user (UserOut shape; the caller refetches
// the list rather than relying on the partial response).
export function updateUserPermissions(
  token: string,
  userId: string,
  permissions: Record<string, boolean>,
): Promise<AdminUserListItem> {
  return request<AdminUserListItem>(`/admin/users/${userId}/permissions`, token, {
    method: "PATCH",
    body: JSON.stringify({ permissions }),
  });
}

export function deleteUser(token: string, userId: string): Promise<void> {
  return request<void>(`/admin/users/${userId}`, token, { method: "DELETE" });
}

// RACCPE-23: RM/RA grant/revoke a BDM's compulsory-training deal-block
// exemption. Unblock-only — the BDM still owes the training. RM/RA only
// (backend require_admin). Callers invalidate the admin users query to refresh
// the indicator.
export type TrainingExemptionStatus = components["schemas"]["TrainingExemptionStatusOut"];

export function grantTrainingExemption(
  token: string,
  userId: string,
): Promise<TrainingExemptionStatus> {
  return request<TrainingExemptionStatus>(`/admin/users/${userId}/training-exemption`, token, {
    method: "POST",
  });
}

export function revokeTrainingExemption(
  token: string,
  userId: string,
): Promise<TrainingExemptionStatus> {
  return request<TrainingExemptionStatus>(`/admin/users/${userId}/training-exemption`, token, {
    method: "DELETE",
  });
}

// CHP-276: re-mint + re-send the one-time set-password invite for an invited
// user. Returns the fresh link so the admin can copy it (mail fallback).
export function resendInvite(token: string, userId: string): Promise<AdminInviteResponse> {
  return request<AdminInviteResponse>(`/admin/users/${userId}/resend-invite`, token, {
    method: "POST",
  });
}

export function reassignDeal(token: string, dealId: string, newBdmId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/reassign`, token, {
    method: "PATCH",
    body: JSON.stringify({ new_bdm_id: newBdmId }),
  });
}

export function cancelSpifRelease(token: string, claimId: string): Promise<SpifClaim> {
  return request<SpifClaim>(`/spif-claims/${claimId}/cancel-release`, token, {
    method: "POST",
  });
}

export function listAdminOrganizations(token: string): Promise<Organization[]> {
  return request<Organization[]>("/admin/organizations", token);
}

// CHP-768: only *registered* Channel Partner organisations (those with an
// onboarded Program Owner who has accepted the T&Cs). Backs the "add a BDM"
// dropdown in InviteUserDialog — a BDM must join an org that already exists.
// Gated require_manager_or_admin so a CM can populate it too.
export function listRegisteredOrganizations(token: string): Promise<Organization[]> {
  return request<Organization[]>("/admin/organizations/registered", token);
}

export function inviteUser(
  token: string,
  payload: AdminInvitePayload,
): Promise<AdminInviteResponse> {
  return request<AdminInviteResponse>("/admin/users/invite", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// M3: PATCH a profile (admin can edit any; CM can edit own assigned CPs).
// Admin Hub Edit-Profile: `organization_id` relinks the firm to an existing
// Organization (the dialog's org <Select>); mutually exclusive with the
// free-text company_name path (backend 422s if both are sent).
export function patchProfile(
  token: string,
  userId: string,
  payload: Partial<ProfileUpdatePayload> & {
    partner_tier?: PartnerTier | null;
    partner_tier_since?: string | null;
    assigned_cm_id?: string | null;
    organization_id?: string | null;
  },
): Promise<Profile> {
  return request<Profile>(`/profiles/${userId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// M3: avatar upload (multipart) + delete.
export async function uploadAvatar(token: string, file: File): Promise<Profile> {
  const fd = new FormData();
  fd.append("file", file);
  // FormData → don't set Content-Type, browser handles boundary.
  const url = `${baseUrl}/profiles/me/avatar`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  return (await resp.json()) as Profile;
}

export function deleteAvatar(token: string): Promise<Profile> {
  return request<Profile>("/profiles/me/avatar", token, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// M2: T&Cs gate
// ---------------------------------------------------------------------------
export type TermsResponse = components["schemas"]["TermsResponse"];

export function fetchTerms(): Promise<TermsResponse> {
  return request<TermsResponse>("/legal/terms", undefined);
}

/**
 * Absolute URL to the T&Cs PDF bytes (CHP-1245 / ADR-0048). Unauthenticated,
 * like /legal/terms — handed straight to the pdf.js viewer's <Document file>.
 */
export function termsPdfUrl(): string {
  return `${baseUrl}/legal/terms/pdf`;
}

export function acceptTerms(token: string): Promise<void> {
  return request<void>("/auth/accept-terms", token, { method: "POST" });
}

// Account Creation slice 1: persists wizard step state pre-T&C.
export function submitOnboarding(token: string, payload: OnboardingUpdatePayload): Promise<void> {
  return request<void>("/auth/onboarding", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Account Creation slice 3: PO bulk-invites BDMs under their org.
export function submitTeamInvites(
  token: string,
  payload: TeamInvitePayload,
): Promise<TeamInviteResponse> {
  return request<TeamInviteResponse>("/partners/me/team-invites", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// M2: Public access-request flow
// ---------------------------------------------------------------------------
export type ReferralCodeVerifyResponse = components["schemas"]["ReferralCodeVerifyResponse"];

export function verifyReferralCode(code: string): Promise<ReferralCodeVerifyResponse> {
  return request<ReferralCodeVerifyResponse>("/access-requests/verify-code", undefined, {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export type AccessRequestSubmitPayload = components["schemas"]["AccessRequestSubmit"];

export type AccessRequestCreated = components["schemas"]["AccessRequestCreated"];

export function submitAccessRequest(
  payload: AccessRequestSubmitPayload,
): Promise<AccessRequestCreated> {
  return request<AccessRequestCreated>("/access-requests", undefined, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// ADR-0010: PRM-rendered login + recovery flow.
// ---------------------------------------------------------------------------

export type LoginRequestPayload = components["schemas"]["LoginRequest"];

export type LoginRedirect = components["schemas"]["LoginRedirect"];

// CHP-508: first-login TOTP enrolment data — the otpauth provisioning URL.
export type MfaEnrolment = components["schemas"]["MfaEnrolment"];

// CHP-508: 202 response when the login flow paused at the MFA gate (staff TOTP).
// `kind` tells the SPA whether to show just a code box ("validate") or the
// enrolment key + a code box ("enrol").
export type MfaChallenge = components["schemas"]["MfaChallenge"];

export type LoginResult = LoginRedirect | MfaChallenge;

export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return "mfa_required" in result;
}

export function login(payload: LoginRequestPayload): Promise<LoginResult> {
  return request<LoginResult>("/auth/login", undefined, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type LoginMfaPayload = components["schemas"]["LoginMfaRequest"];

// CHP-508: complete a paused MFA login. Returns the redirect on success, or
// another MfaChallenge if the flow chained a second factor step.
export function loginMfa(payload: LoginMfaPayload): Promise<LoginResult> {
  return request<LoginResult>("/auth/login/mfa", undefined, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function forgotPassword(email: string): Promise<void> {
  return request<void>("/auth/forgot-password", undefined, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export type SetPasswordPayload = components["schemas"]["SetPasswordRequest"];

export function setPassword(payload: SetPasswordPayload): Promise<LoginRedirect> {
  return request<LoginRedirect>("/auth/set-password", undefined, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// M2: Admin queue + referral-code CRUD
// ---------------------------------------------------------------------------
export type AccessRequestStatus = components["schemas"]["AccessRequestStatus"];

export type AccessRequest = components["schemas"]["AccessRequestOut"];

export function listAccessRequests(
  token: string,
  params: URLSearchParams = new URLSearchParams(),
): Promise<AccessRequest[]> {
  const qs = params.toString();
  return request<AccessRequest[]>(`/admin/access-requests${qs ? `?${qs}` : ""}`, token);
}

export function approveAccessRequest(token: string, id: string): Promise<AdminInviteResponse> {
  return request<AdminInviteResponse>(`/admin/access-requests/${id}/approve`, token, {
    method: "POST",
  });
}

export function rejectAccessRequest(
  token: string,
  id: string,
  reason: string,
): Promise<AccessRequest> {
  return request<AccessRequest>(`/admin/access-requests/${id}/reject`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export type ReferralCode = components["schemas"]["ReferralCodeOut"];

export type ReferralCodeCreatePayload = components["schemas"]["ReferralCodeCreate"];

export function listReferralCodes(token: string, activeOnly = false): Promise<ReferralCode[]> {
  const qs = activeOnly ? "?active_only=true" : "";
  return request<ReferralCode[]>(`/admin/referral-codes${qs}`, token);
}

export function createReferralCode(
  token: string,
  payload: ReferralCodeCreatePayload,
): Promise<ReferralCode> {
  return request<ReferralCode>("/admin/referral-codes", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deactivateReferralCode(token: string, id: string): Promise<void> {
  return request<void>(`/admin/referral-codes/${id}`, token, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Epic 4: SPIF claims
// ---------------------------------------------------------------------------
// Slice 0018: 'cancelled' is for approved-but-not-released claims withdrawn
// by a CM during the user-deactivation gate. Distinct from 'rejected' (which
// is a CM decision on a pending claim).
export type SpifClaimStatus = components["schemas"]["SpifClaimStatus"];

export type SpifClaim = components["schemas"]["SpifClaimOut"];

export type SpifClaimCreatePayload = components["schemas"]["SpifClaimCreate"];

export function submitSpifClaim(
  token: string,
  payload: SpifClaimCreatePayload,
): Promise<SpifClaim> {
  return request<SpifClaim>("/spif-claims", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listMySpifClaims(token: string): Promise<SpifClaim[]> {
  return request<SpifClaim[]>("/spif-claims/me", token);
}

export function listPendingSpifClaims(token: string): Promise<SpifClaim[]> {
  return request<SpifClaim[]>("/spif-claims/pending", token);
}

export function getSpifClaim(token: string, id: string): Promise<SpifClaim> {
  return request<SpifClaim>(`/spif-claims/${id}`, token);
}

export function approveSpifClaim(token: string, id: string): Promise<SpifClaim> {
  return request<SpifClaim>(`/spif-claims/${id}/approve`, token, {
    method: "POST",
  });
}

export function rejectSpifClaim(token: string, id: string, reason: string): Promise<SpifClaim> {
  return request<SpifClaim>(`/spif-claims/${id}/reject`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ---------------------------------------------------------------------------
// po-dashboard: Program-Owner org-scoped SPIF reads (READ-ONLY — there is no
// Mark-Paid here; SPIF settlement stays ResetData-staff-only). Shapes MUST
// match apps/api/.../schemas/po_rewards.py field-for-field.
// ---------------------------------------------------------------------------

/** One row of the PO team SPIF ledger (GET /po/spif/team). The drawer columns
 * (Deal ID / Client / Earner / Type / Amount / Status) are server-enriched off
 * the claim's FK joins, so no per-row fetch is needed. `spif_type` is the
 * deal's service_offering (no first-class SpifClaim type) — rendered as-is in
 * the "Type" column. */
export type POTeamSpifRow = components["schemas"]["POTeamSpifRow"];

/** Pending vs Paid-YTD aggregate totals for the PO's org (GET /po/spif/buckets).
 * Amounts are in CENTS. */
export type POSpifBuckets = components["schemas"]["POSpifBuckets"];

/** One audit/history row in a per-earner SPIF timeline. `payload` is the raw
 * audit JSON for the FE to render contextual detail. */
export type POSpifAuditEntry = components["schemas"]["POSpifAuditEntry"];

/** One org member's itemised SPIF contributions + history timeline
 * (GET /po/spif/earners/{earner_id}). `earner_role`/`earner_role_label` are
 * the org access level (PO vs BDM, derived from is_org_admin server-side —
 * same idiom as TeamMemberDetail.role/role_label); the drawer banner shows
 * name + role + total accrued (the last summed client-side off `claims`). */
export type POEarnerSpifDetail = components["schemas"]["POEarnerSpifDetail"];

/** Optional ledger filters — `status` and `spif_type` (the deal's
 * service_offering). Omit either to leave it unfiltered. */
export interface TeamSpifQuery {
  status?: SpifClaimStatus;
  spif_type?: string;
}

export function getTeamSpif(token: string, opts: TeamSpifQuery = {}): Promise<POTeamSpifRow[]> {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.spif_type) params.set("spif_type", opts.spif_type);
  const qs = params.toString();
  return request<POTeamSpifRow[]>(`/po/spif/team${qs ? `?${qs}` : ""}`, token);
}

export function getSpifBuckets(token: string): Promise<POSpifBuckets> {
  return request<POSpifBuckets>("/po/spif/buckets", token);
}

export function getEarnerSpifDetail(token: string, earnerId: string): Promise<POEarnerSpifDetail> {
  return request<POEarnerSpifDetail>(`/po/spif/earners/${earnerId}`, token);
}

// ---------------------------------------------------------------------------
// Asset library (the "Assets Hub")
// ---------------------------------------------------------------------------
// "asset" is the frozen codename; the user-facing name lives in
// lib/asset-labels.ts. Shapes MUST match apps/api/.../schemas/asset.py.
export type Audience = components["schemas"]["Audience"];
export type DocumentType = components["schemas"]["DocumentType"];
export type AssetStatus = components["schemas"]["AssetStatus"];
export type TaxonomyKind = components["schemas"]["TaxonomyKind"];

export type TaxonomyTerm = components["schemas"]["TaxonomyTermOut"];

export type AssetVersion = components["schemas"]["AssetVersionOut"];

export type AssetCard = components["schemas"]["AssetCardOut"];

export type AssetDetail = components["schemas"]["AssetDetailOut"];

export type FacetCount = components["schemas"]["FacetCount"];

export type AssetFacets = components["schemas"]["AssetFacets"];

export type AssetListResponse = components["schemas"]["AssetListResponse"];

export type StarredAssetsResponse = components["schemas"]["StarredAssetsResponse"];

export type AssetUpdatePayload = components["schemas"]["AssetUpdate"];

export type AssetSharePayload = components["schemas"]["AssetShareRequest"];

export function listAssets(
  token: string,
  params: URLSearchParams = new URLSearchParams(),
): Promise<AssetListResponse> {
  const qs = params.toString();
  return request<AssetListResponse>(`/assets${qs ? `?${qs}` : ""}`, token);
}

export function listAssetTaxonomy(token: string, kind?: TaxonomyKind): Promise<FacetCount[]> {
  const qs = kind ? `?kind=${kind}` : "";
  return request<FacetCount[]>(`/assets/taxonomy${qs}`, token);
}

/** Create (or reuse) a Topic/Industry term in-flow — the "Other" affordance
 * (CHP-392). RM/RA only. The server deduplicates case-insensitively, so a
 * label matching an existing term returns that term rather than a duplicate. */
export function createAssetTaxonomy(
  token: string,
  payload: { kind: TaxonomyKind; label: string },
): Promise<TaxonomyTerm> {
  return request<TaxonomyTerm>(`/assets/taxonomy`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Rename a Topic/Industry term (CHP-392). RM/RA only. The server rejects a
 * rename that collides with another term of the same kind (409). */
export function updateAssetTaxonomy(
  token: string,
  id: string,
  payload: { label: string },
): Promise<TaxonomyTerm> {
  return request<TaxonomyTerm>(`/assets/taxonomy/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete a Topic/Industry term (CHP-392). RM/RA only. The server rejects
 * deletion of a term that's still in use by an asset (409). */
export function deleteAssetTaxonomy(token: string, id: string): Promise<void> {
  return request<void>(`/assets/taxonomy/${id}`, token, { method: "DELETE" });
}

export function listStarredAssets(token: string): Promise<StarredAssetsResponse> {
  return request<StarredAssetsResponse>(`/assets/starred`, token);
}

/** One "New Sales Collateral Added" feed item (follow-up #62). A lean
 * projection of an asset — shape MUST match schemas/asset.py::RecentAssetOut. */
export type RecentAsset = components["schemas"]["RecentAssetOut"];

export type RecentAssetsResponse = components["schemas"]["RecentAssetsResponse"];

/** Recently-published assets, newest-first — the BDM dashboard's
 * "New Sales Collateral Added" feed source. NB the backend currently uses a
 * recent-window proxy (default 14 days), not a true since-last-visit boundary;
 * see schemas/asset.py::RecentAssetsResponse + the router's limitation note. */
export function listRecentAssets(token: string): Promise<RecentAssetsResponse> {
  return request<RecentAssetsResponse>(`/assets/recent`, token);
}

export function getAsset(token: string, id: string): Promise<AssetDetail> {
  return request<AssetDetail>(`/assets/${id}`, token);
}

export function starAsset(token: string, id: string): Promise<void> {
  return request<void>(`/assets/${id}/star`, token, { method: "POST" });
}

export function unstarAsset(token: string, id: string): Promise<void> {
  return request<void>(`/assets/${id}/star`, token, { method: "DELETE" });
}

export function shareAsset(token: string, id: string, payload: AssetSharePayload): Promise<void> {
  return request<void>(`/assets/${id}/share`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Fetch an asset's current-version bytes as an object URL for the Quick-View
 * lightbox. The preview/download endpoints require a bearer token, so we
 * can't point an <iframe>/<a> straight at them — fetch + Blob instead. Caller
 * MUST revoke the returned URL when done.
 */
export async function previewAsset(
  token: string,
  id: string,
): Promise<{ objectUrl: string; contentType: string }> {
  const resp = await fetch(`${baseUrl}/assets/${id}/preview`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new ApiError(`API ${resp.status}`, resp.status, null);
  const blob = await resp.blob();
  return {
    objectUrl: URL.createObjectURL(blob),
    contentType: resp.headers.get("content-type") ?? "application/octet-stream",
  };
}

export async function downloadAsset(token: string, id: string, filename: string): Promise<void> {
  const resp = await fetch(`${baseUrl}/assets/${id}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new ApiError(`API ${resp.status}`, resp.status, null);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement("a");
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Asset management (RM/RA only) ----------------------------------------
export interface AssetCreateFields {
  title: string;
  audience: Audience;
  document_type: DocumentType;
  description?: string;
  topic_ids?: string[];
  industry_ids?: string[];
  /** Optional hero/thumbnail image (png/jpeg); omitted → icon fallback (CHP-348). */
  hero_image?: File;
}

export async function createAsset(
  token: string,
  file: File,
  fields: AssetCreateFields,
): Promise<AssetDetail> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", fields.title);
  fd.append("audience", fields.audience);
  fd.append("document_type", fields.document_type);
  if (fields.description) fd.append("description", fields.description);
  for (const t of fields.topic_ids ?? []) fd.append("topic_ids", t);
  for (const i of fields.industry_ids ?? []) fd.append("industry_ids", i);
  if (fields.hero_image) fd.append("hero_image", fields.hero_image);
  const resp = await fetch(`${baseUrl}/assets`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  return (await resp.json()) as AssetDetail;
}

export async function uploadAssetVersion(
  token: string,
  id: string,
  file: File,
  changeNotes?: string,
): Promise<AssetDetail> {
  const fd = new FormData();
  fd.append("file", file);
  if (changeNotes) fd.append("change_notes", changeNotes);
  const resp = await fetch(`${baseUrl}/assets/${id}/versions`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!resp.ok) throw new ApiError(`API ${resp.status}`, resp.status, null);
  return (await resp.json()) as AssetDetail;
}

export function updateAsset(
  token: string,
  id: string,
  payload: AssetUpdatePayload,
): Promise<AssetDetail> {
  return request<AssetDetail>(`/assets/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function publishAsset(token: string, id: string): Promise<AssetDetail> {
  return request<AssetDetail>(`/assets/${id}/publish`, token, { method: "POST" });
}

export function archiveAsset(token: string, id: string): Promise<AssetDetail> {
  return request<AssetDetail>(`/assets/${id}/archive`, token, { method: "POST" });
}

export function deleteAsset(token: string, id: string): Promise<void> {
  return request<void>(`/assets/${id}`, token, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Deals — Flows B/C/D/E
// ---------------------------------------------------------------------------

export type DealStatus = components["schemas"]["DealStatus"];

export type IndustryVertical = components["schemas"]["IndustryVertical"];

export type DealConflictStatus = components["schemas"]["DealConflictStatus"];

export type CloseReasonCategory = components["schemas"]["CloseReasonCategory"];

export type ServiceOffering = components["schemas"]["ServiceOffering"];

export type Deal = components["schemas"]["DealOut"];

/** GET /deals/{id} response for CM/RM/RA callers (partners get Deal). */
// The backend returns ManagerDealOut(DealOut) here, but GET /deals/{deal_id}
// declares `response_model=None`, so these two fields never reach the OpenAPI
// schema and codegen can't generate them — hand-typed as an intersection over
// the generated DealOut until that route gets a real response_model.
export type ManagerDeal = components["schemas"]["DealOut"] & {
  review_internal_notes: string | null;
  sla_holder_name: string | null;
};

/** BDM/PO outcome feed-card discriminator (CHP-304) — mirrors the backend
 * `DealOutcomeKind`. Registration- vs extension-approved are split via the
 * `extension_approved_at`/`extension_amended` markers (migration 0029); see
 * `last_outcome_kind` in apps/api/.../schemas/deal.py. */
export type DealOutcomeKind =
  | "registration_approved"
  | "registration_assigned"
  | "registration_declined"
  | "extension_approved"
  | "extension_approved_amended"
  | "extension_declined"
  | "closed_won_approved"
  | "closed_won_declined";

export type DealListItem = components["schemas"]["DealListItem"];

export type ManagerDealSegment = "all" | "new_registrations" | "extensions" | "closed_won_reviews";

export type ManagerDealListItem = components["schemas"]["ManagerDealListItem"];

export type DealDraftCreatePayload = components["schemas"]["DealDraftCreatePayload"];

export type DealDraftUpdatePayload = components["schemas"]["DealDraftUpdatePayload"];

export type DealConflictMatch = components["schemas"]["DealConflictMatch"];

export type DealConflictResult = components["schemas"]["DealConflictResult"];

export type AssignableBdm = components["schemas"]["AssignableBdmOut"];

export type DealDashboardMetrics = components["schemas"]["DealDashboardMetrics"];

export type DealWinSubmitPayload = components["schemas"]["DealWinSubmitPayload"];

export type DealWinApprovePayload = components["schemas"]["DealWinApprovePayload"];

export type DealClosePayload = components["schemas"]["DealClosePayload"];

export type DealSpifAdjudicatePayload = components["schemas"]["DealSpifAdjudicatePayload"];

export type DealAdminUpdatePayload = components["schemas"]["DealAdminUpdatePayload"];

export type DealExtensionRequestPayload = components["schemas"]["DealExtensionRequestPayload"];

/** CHP-429: the "Deal Progress" activity category. Drives the dynamic fields
 * in the Log-deal-progress modal and the per-category fields in
 * `activity_details`. */
export type DealActivityType = components["schemas"]["DealActivityType"];

/** CHP-340: a saveable, time-stamped note on the deal detail page. CHP-429
 * extends it with optional activity metadata for "Deal Progress" entries. */
export type DealNote = components["schemas"]["DealNoteOut"];

export type DealNoteCreatePayload = components["schemas"]["DealNoteCreatePayload"];

export type DealNoteUpdatePayload = components["schemas"]["DealNoteUpdatePayload"];

/** CHP-401: the adjudication stage an internal note belongs to. */
export type DealInternalNoteContext = components["schemas"]["InternalNoteContext"];

/** CHP-401: a CM/RA/RM-authored internal note. Kept on a separate table from
 * DealNote so the visibility boundary is structural — never returned to a
 * BDM/PO. The closed-won rejection reason is persisted here (CHP-413). */
export type DealInternalNote = components["schemas"]["DealInternalNoteOut"];

export type DealInternalNoteCreatePayload = components["schemas"]["DealInternalNoteCreatePayload"];

export type DealExtensionApprovePayload = components["schemas"]["DealExtensionApprovePayload"];

export type DealExtensionDenyPayload = components["schemas"]["DealExtensionDenyPayload"];

export function createDealDraft(token: string, payload: DealDraftCreatePayload): Promise<Deal> {
  return request<Deal>("/deals/drafts", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateDealDraft(
  token: string,
  dealId: string,
  payload: DealDraftUpdatePayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/draft`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function submitDeal(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/submit`, token, { method: "POST" });
}

export function discardDealDraft(token: string, dealId: string): Promise<void> {
  return request<void>(`/deals/${dealId}/draft`, token, { method: "DELETE" });
}

export function listMyDrafts(token: string): Promise<Deal[]> {
  return request<Deal[]>("/deals/drafts/me", token);
}

export function listDeals(token: string, params?: string): Promise<DealListItem[]> {
  const qs = params ? `?${params}` : "";
  return request<DealListItem[]>(`/deals${qs}`, token);
}

/** @deprecated Use listDeals instead */
export function listMyDeals(token: string): Promise<DealListItem[]> {
  return request<DealListItem[]>("/deals/me", token);
}

export function listManagerDeals(
  token: string,
  segment: ManagerDealSegment = "all",
): Promise<ManagerDealListItem[]> {
  return request<ManagerDealListItem[]>(`/deals/manager-list?segment=${segment}`, token);
}

export function getDeal(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}`, token);
}

export function listAssignableBdms(token: string): Promise<AssignableBdm[]> {
  return request<AssignableBdm[]>("/deals/assignable-bdms", token);
}

export function approveDeal(
  token: string,
  dealId: string,
  opts?: { internal_notes?: string },
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/approve`, token, {
    method: "POST",
    ...(opts?.internal_notes ? { body: JSON.stringify(opts) } : {}),
  });
}

export function denyDeal(
  token: string,
  dealId: string,
  reason: string,
  opts?: { internal_notes?: string; routed_to_deal_id?: string },
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/deny`, token, {
    method: "POST",
    body: JSON.stringify({ reason, ...opts }),
  });
}

/** Open a deal for review: pauses SLA sweeps ~30 min and collision-locks
 * it ("Under Review by [Name]"). Re-POST to heartbeat; 409 (detail carries
 * the holder's name) if another reviewer holds an unexpired lock. */
export function holdDealReview(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/review-hold`, token, { method: "POST" });
}

/** Release a review hold (holder or RM while unexpired). */
export function releaseDealReviewHold(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/review-hold/release`, token, { method: "POST" });
}

/** @deprecated Use denyDeal instead */
export function rejectDeal(token: string, dealId: string, reason: string): Promise<Deal> {
  return denyDeal(token, dealId, reason);
}

export function forceBypassDeal(token: string, dealId: string, reason?: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/force-bypass`, token, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function checkDealConflicts(token: string, dealId: string): Promise<DealConflictResult> {
  return request<DealConflictResult>(`/deals/${dealId}/conflict-check`, token);
}

export function fetchDealDashboard(token: string): Promise<DealDashboardMetrics> {
  return request<DealDashboardMetrics>("/deals/dashboard", token);
}

// po-dashboard: Program-Owner team surfaces. /deals/team-dashboard returns the
// same DealDashboardMetrics shape as /deals/dashboard but aggregated across the
// PO's whole organisation; /deals/team-list returns the same ManagerDealListItem
// shape as /deals/manager-list scoped to the org. Both 403 any non-PO server-side
// (BDMs included) — the Insights surface gates on is_org_admin before calling.
export function getTeamDashboard(token: string): Promise<DealDashboardMetrics> {
  return request<DealDashboardMetrics>("/deals/team-dashboard", token);
}

export function getTeamDeals(
  token: string,
  segment: ManagerDealSegment = "all",
): Promise<ManagerDealListItem[]> {
  return request<ManagerDealListItem[]>(`/deals/team-list?segment=${segment}`, token);
}

// ---------------------------------------------------------------------------
// po-dashboard Flow F: PO Team Directory (manage your own org's members).
//
// The PO reads + manages the members of their OWN organisation: list, detail
// (identity + onboarding + access level + history timeline), edit role
// (BDM↔PO), and remove (deactivate — never hard-delete). All under the PO
// namespace (`/partners/me/team/members`). Every shape mirrors the backend's
// `schemas/team_member.py`; keep them in lock-step.
// ---------------------------------------------------------------------------

/** A member's role within their org. Mirrors is_org_admin (true → "po"). */
export type TeamMemberRole = "po" | "bdm";

/** The three Flow-F directory tabs. A read-model grouping over DisplayStatus:
 *  Registered → active; Unregistered → invited|pending; Removed →
 *  deactivated|closed|rejected (see schemas/team_member.py::status_tab_for). */
export type TeamMemberStatusTab = "registered" | "unregistered" | "removed";

export type TeamMemberListItem = components["schemas"]["TeamMemberListItem"];

/** One entry on the member-detail HISTORY & AUDIT timeline. */
export type TeamMemberHistoryEntry = components["schemas"]["MemberHistoryEntry"];

export type TeamMemberDetail = components["schemas"]["TeamMemberDetail"];

export function getTeamMembers(token: string): Promise<TeamMemberListItem[]> {
  return request<TeamMemberListItem[]>("/partners/me/team/members", token);
}

export function getTeamMember(token: string, userId: string): Promise<TeamMemberDetail> {
  return request<TeamMemberDetail>(`/partners/me/team/members/${userId}`, token);
}

// PATCH role — flip BDM↔PO. 422 on a no-op (already the target role); 409
// {code:"last_po", message} when demoting the org's sole PO.
export function updateTeamMemberRole(
  token: string,
  userId: string,
  role: TeamMemberRole,
): Promise<TeamMemberDetail> {
  return request<TeamMemberDetail>(`/partners/me/team/members/${userId}/role`, token, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

// POST remove — deactivate (never hard-delete). 422 on self-removal; 409 for
// an already-non-active member; 409 {code:"last_po", message} for the sole PO.
export function removeTeamMember(
  token: string,
  userId: string,
  note?: string | null,
): Promise<TeamMemberDetail> {
  return request<TeamMemberDetail>(`/partners/me/team/members/${userId}/remove`, token, {
    method: "POST",
    body: JSON.stringify(note != null ? { note } : {}),
  });
}

// POST resend-invite — re-send the set-password invitation to a not-yet-onboarded
// (Pending) org member. 409 if the member is already onboarded; 404 cross-org.
export function resendTeamMemberInvite(token: string, userId: string): Promise<TeamMemberDetail> {
  return request<TeamMemberDetail>(`/partners/me/team/members/${userId}/resend-invite`, token, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// Flow E: Win / Close / SPIF

export function submitDealForWin(
  token: string,
  dealId: string,
  payload: DealWinSubmitPayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/win-submit`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function approveDealWin(
  token: string,
  dealId: string,
  payload: DealWinApprovePayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/win-approve`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function rejectDealWin(token: string, dealId: string, reason: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/win-reject`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function closeDeal(token: string, dealId: string, payload: DealClosePayload): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/close`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function adjudicateDealSpif(
  token: string,
  dealId: string,
  eligible: boolean,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/spif-adjudicate`, token, {
    method: "POST",
    body: JSON.stringify({ eligible }),
  });
}

export function settleDealSpif(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/spif-settle`, token, { method: "POST" });
}

export function adminUpdateDeal(
  token: string,
  dealId: string,
  payload: DealAdminUpdatePayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/admin-update`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function listWonDeals(token: string): Promise<Deal[]> {
  return request<Deal[]>("/deals/won", token);
}

// Flow D: Extension / Expiry

export function listExpiringDeals(token: string): Promise<Deal[]> {
  return request<Deal[]>("/deals/expiring", token);
}

export function requestDealExtension(
  token: string,
  dealId: string,
  payload: DealExtensionRequestPayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/request-extension`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function approveDealExtension(
  token: string,
  dealId: string,
  payload: DealExtensionApprovePayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/approve-extension`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function denyDealExtension(
  token: string,
  dealId: string,
  reason: string,
  internalNotes?: string | null,
): Promise<Deal> {
  const payload: DealExtensionDenyPayload = { reason };
  if (internalNotes != null && internalNotes.trim() !== "") {
    payload.internal_notes = internalNotes.trim();
  }
  return request<Deal>(`/deals/${dealId}/deny-extension`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function withdrawDealExtension(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/withdraw-extension`, token, { method: "POST" });
}

export function acknowledgeDealExpiry(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/acknowledge-expiry`, token, { method: "POST" });
}

// Feed-card dismissals (Wave 2 — partner feed & dashboard). Non-destructive:
// the backend stamps draft_dismissed_at / outcome_dismissed_at so the feed
// stops rendering the card; the deal itself is untouched. Both idempotent.

export function dismissDraftCard(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/dismiss-draft-card`, token, { method: "POST" });
}

/** CHP-369/371/375: CM/RA/RM dismisses a deal's manager-feed card. */
export function dismissManagerCard(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/dismiss-manager-card`, token, { method: "POST" });
}

export function dismissOutcomeCard(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/dismiss-outcome-card`, token, { method: "POST" });
}

/** CHP-599: owner dismisses the post-expiry "Add context" card — stamps
 * expired_context_dismissed_at so the dismissal holds across BOTH partner
 * dashboards (previously session-only client state). */
export function dismissExpiredContextCard(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/dismiss-expired-context-card`, token, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Deal expiry notice / post-expiry context (CHP-394 / CHP-380)
// ---------------------------------------------------------------------------

/** CHP-394: a CM/RA/RM clicks "Remind me in 7 days" on a deal's "Deal expiry
 * notice" feed card — stamps `expiry_notice_snoozed_until = now + 7 days`,
 * suppressing the 14-day card until then (shared across the manager pool). */
export function snoozeExpiryNotice(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/snooze-expiry-notice`, token, { method: "POST" });
}

/** CHP-380: owner OR CM/RA/RM records the close reason on an auto-expired
 * CLOSED_LOST deal — stamps `expiry_context_added_at`, auto-dismissing the
 * "Add context" card. Same body shape as the close-lost payload. */
export type DealAddExpiryContextPayload = components["schemas"]["DealAddExpiryContextPayload"];

export function addExpiryContext(
  token: string,
  dealId: string,
  payload: DealAddExpiryContextPayload,
): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/add-expiry-context`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// In-app notifications (CHP-1193 — Account Creation Flow A/B to-do feed)
//
// Read-side of the append-only notifications primitive: a caller only ever
// sees their OWN rows, and the only mutation is stamping read_at. Shapes
// mirror apps/api/.../schemas/notification.py (NotificationOut).
// ---------------------------------------------------------------------------

export type NotificationItem = components["schemas"]["NotificationOut"];

export function listMyNotifications(
  token: string,
  opts: { unreadOnly?: boolean } = {},
): Promise<NotificationItem[]> {
  const qs = opts.unreadOnly ? "?unread_only=true" : "";
  return request<NotificationItem[]>(`/notifications/me${qs}`, token);
}

export function markNotificationRead(token: string, id: string): Promise<NotificationItem> {
  return request<NotificationItem>(`/notifications/${id}/read`, token, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Meeting rewards (CHP-382 / CHP-391)
//
// A BDM/PO requests a flat-$100 reward against a LIVE deal after a qualifying
// customer meeting; a CM/RA/RM approves or rejects. The list backs the
// closed-won "Rewards earned" surface (CHP-391). Shapes mirror
// apps/api/.../schemas/meeting_reward.py.
// ---------------------------------------------------------------------------

export type MeetingRewardStatus = components["schemas"]["MeetingRewardStatus"];

export type MeetingRewardAttendee = components["schemas"]["MeetingRewardAttendee"];

export type MeetingRewardClaim = components["schemas"]["MeetingRewardClaimOut"];

/** Request body for POST /deals/{id}/meeting-reward (CHP-387/CHP-464). The
 * backend requires ≥1 attendee and non-empty discussion/next-steps; attendee
 * title is optional on THIS schema (unlike the progress-log meeting form). */
export type MeetingRewardClaimCreatePayload = components["schemas"]["MeetingRewardClaimCreate"];

/** CHP-464 (finishing CHP-387): owner/creator (BDM/PO) claims the flat-$100
 * first-meeting reward on a LIVE deal — the direct-payout card's "Claim
 * reward" action. 409 unless LIVE; leak-safe 404 outside the deal's org. */
export function requestMeetingReward(
  token: string,
  dealId: string,
  payload: MeetingRewardClaimCreatePayload,
): Promise<MeetingRewardClaim> {
  return request<MeetingRewardClaim>(`/deals/${dealId}/meeting-reward`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listMeetingRewards(token: string, dealId: string): Promise<MeetingRewardClaim[]> {
  return request<MeetingRewardClaim[]>(`/deals/${dealId}/meeting-reward`, token);
}

export function approveMeetingReward(
  token: string,
  dealId: string,
  claimId: string,
): Promise<MeetingRewardClaim> {
  return request<MeetingRewardClaim>(`/deals/${dealId}/meeting-reward/${claimId}/approve`, token, {
    method: "POST",
  });
}

export function rejectMeetingReward(
  token: string,
  dealId: string,
  claimId: string,
  reason: string,
): Promise<MeetingRewardClaim> {
  return request<MeetingRewardClaim>(`/deals/${dealId}/meeting-reward/${claimId}/reject`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ---------------------------------------------------------------------------
// First-meeting-reward midpoint reminder snooze/stop (CHP-387)
// ---------------------------------------------------------------------------

/** CHP-387: owner/creator or org-PO clicks "Remind me in 14 days" on a deal's
 * 45-day-midpoint "First meeting reward status" card — stamps
 * `meeting_reward_reminder_snoozed_until = now + 14 days`, hiding the card until
 * then. 409 unless the deal is LIVE; leak-safe 404 outside the deal's org. */
export function snoozeMeetingRewardReminder(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/snooze-meeting-reward-reminder`, token, {
    method: "POST",
  });
}

/** CHP-387: owner/creator or org-PO clicks "Stop reminders" on a deal's
 * midpoint card — stamps `meeting_reward_reminder_stopped_at`, so the card never
 * re-surfaces for this deal. 409 unless the deal is LIVE; leak-safe 404 outside
 * the deal's org. */
export function stopMeetingRewardReminder(token: string, dealId: string): Promise<Deal> {
  return request<Deal>(`/deals/${dealId}/stop-meeting-reward-reminder`, token, {
    method: "POST",
  });
}

// Deal notes (CHP-340). Owner/creator (BDM/PO) authors and edits within the
// 5-minute window; CM/RA/RM read-only. Notes are append-only deal history.

export function listDealNotes(token: string, dealId: string): Promise<DealNote[]> {
  return request<DealNote[]>(`/deals/${dealId}/notes`, token);
}

export function addDealNote(
  token: string,
  dealId: string,
  payload: DealNoteCreatePayload,
): Promise<DealNote> {
  return request<DealNote>(`/deals/${dealId}/notes`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateDealNote(
  token: string,
  dealId: string,
  noteId: string,
  payload: DealNoteUpdatePayload,
): Promise<DealNote> {
  return request<DealNote>(`/deals/${dealId}/notes/${noteId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// Internal notes (CHP-401 / CHP-413). CM/RA/RM only — manager-gated on the
// backend; a BDM/PO never reaches these endpoints (structural leak boundary).

export function listDealInternalNotes(token: string, dealId: string): Promise<DealInternalNote[]> {
  return request<DealInternalNote[]>(`/deals/${dealId}/internal-notes`, token);
}

export function addDealInternalNote(
  token: string,
  dealId: string,
  payload: DealInternalNoteCreatePayload,
): Promise<DealInternalNote> {
  return request<DealInternalNote>(`/deals/${dealId}/internal-notes`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Training (RACCPE-2 / Slice 1)
//
// Two read endpoints back the trainee surface: list + detail. The shapes
// here mirror `apps/api/src/prm_api/schemas/training.py` exactly. Two
// deliberate omissions vs the ORM (locked in the backend schemas, pinned
// by test_training_modules.py — DO NOT add these back without changing
// the backend at the same time):
//   - TrainingQuizOption has NO `is_correct` (answer-key leak; trainees
//     would trivially pass any quiz).
//   - TrainingBlock has NO `storage_key` (raw MinIO path; trainees fetch
//     assets via the presigned-URL endpoint planned for Slice 4).
// Slice 2's authoring API will add separate admin-scoped interfaces
// (e.g. TrainingQuizOptionAdmin) that include these fields.
// ---------------------------------------------------------------------------

export type TrainingModuleStatus = components["schemas"]["ModuleStatus"];

export type TrainingBlockType = components["schemas"]["BlockType"];

export type TrainingQuizOption = components["schemas"]["TrainingQuizOptionOut"];

export type TrainingQuizQuestion = components["schemas"]["TrainingQuizQuestionOut"];

export type TrainingBlock = components["schemas"]["TrainingBlockOut"];

export type TrainingModuleVersion = components["schemas"]["TrainingModuleVersionOut"];

// CHP-985: when a compulsory module starts locking partners out — grace_days
// counts from publish (the long-standing behaviour), immediate locks on
// publish, fixed_date locks at enforcement_date regardless of publish time.
export type TrainingEnforcementMode = components["schemas"]["EnforcementMode"];

export type TrainingModuleListItem = components["schemas"]["TrainingModuleListItem"];

export type TrainingModule = components["schemas"]["TrainingModuleOut"];

export function listTrainingModules(token: string): Promise<TrainingModuleListItem[]> {
  return request<TrainingModuleListItem[]>("/training/modules", token);
}

export function getTrainingModule(token: string, moduleId: string): Promise<TrainingModule> {
  return request<TrainingModule>(`/training/modules/${moduleId}`, token);
}

// CHP-1049: RA read-only preview — the trainee module shape PLUS the quiz answer
// key (`is_correct`). Backed by `GET /training/preview/modules/{id}`, gated
// `require_admin` (RA + RM only). Mirrors TrainingModule field-for-field; the
// only divergence is quiz options carrying `is_correct` so the RA sees which
// answer is correct. Blocks still omit `storage_key` (not authoring).
export type TrainingQuizOptionPreview = components["schemas"]["TrainingQuizOptionAdminOut"];

export type TrainingQuizQuestionPreview = components["schemas"]["TrainingQuizQuestionAdminOut"];

export type TrainingModuleVersionPreview = components["schemas"]["TrainingModuleVersionPreviewOut"];

export type TrainingModulePreview = components["schemas"]["TrainingModulePreviewOut"];

export function getTrainingModulePreview(
  token: string,
  moduleId: string,
): Promise<TrainingModulePreview> {
  return request<TrainingModulePreview>(`/training/preview/modules/${moduleId}`, token);
}

// RACCPE-6 / Slice 4: short-lived presigned URL for a published module's media
// block, handed to the trainee player's <video>/<img>/PDF embed. Any active
// user may call it (backend gates require_terms_accepted).
export type TrainingBlockAssetUrl = components["schemas"]["TrainingBlockAssetUrlOut"];

// CHP-1184: presigned direct-to-MinIO upload URL for a media block.
export type TrainingBlockUploadUrl = components["schemas"]["TrainingBlockUploadUrlOut"];

export function getTrainingBlockAssetUrl(
  token: string,
  moduleId: string,
  blockId: string,
): Promise<TrainingBlockAssetUrl> {
  return request<TrainingBlockAssetUrl>(
    `/training/modules/${moduleId}/blocks/${blockId}/asset-url`,
    token,
  );
}

// CHP-421: presigned URL for a block in ANY version of a module — the admin
// preview uses this so a draft/revision's media renders (the trainee endpoint
// above serves the published current version only). require_admin on the backend.
export function getAdminTrainingBlockAssetUrl(
  token: string,
  moduleId: string,
  blockId: string,
): Promise<TrainingBlockAssetUrl> {
  return request<TrainingBlockAssetUrl>(
    `/training/admin/modules/${moduleId}/blocks/${blockId}/asset-url`,
    token,
  );
}

// ---------------------------------------------------------------------------
// Training completion engine (RACCPE-14 / Slice 5) — trainee-facing watch
// progress, quiz submission/scoring, and per-module status. All gated
// `require_terms_accepted` on the backend.
// ---------------------------------------------------------------------------

export type TrainingBlockProgress = components["schemas"]["TrainingBlockProgressOut"];

// Idempotent high-water-mark upsert: the player posts the max currentTime seen
// so far; the backend keeps the larger value and flips completion at ≥95%.
export function postTrainingBlockProgress(
  token: string,
  blockId: string,
  watchedSeconds: number,
): Promise<TrainingBlockProgress> {
  return request<TrainingBlockProgress>(`/training/blocks/${blockId}/progress`, token, {
    method: "POST",
    body: JSON.stringify({ watched_seconds: watchedSeconds }),
  });
}

// RACCPE-28: the current user's progress against a module's current version —
// which blocks they've completed, whether the quiz is passed, and whether the
// module is complete. Drives the player's completion checklist + quiz gate.
export type TrainingModuleProgress = components["schemas"]["TrainingModuleProgressOut"];

export function getTrainingModuleProgress(
  token: string,
  moduleId: string,
): Promise<TrainingModuleProgress> {
  return request<TrainingModuleProgress>(`/training/modules/${moduleId}/progress`, token);
}

export type TrainingQuizAnswerInput = components["schemas"]["TrainingQuizAnswerIn"];

export type TrainingQuizAttemptResult = components["schemas"]["TrainingQuizAttemptOut"];

export function submitTrainingQuizAttempt(
  token: string,
  moduleId: string,
  answers: TrainingQuizAnswerInput[],
): Promise<TrainingQuizAttemptResult> {
  return request<TrainingQuizAttemptResult>(`/training/modules/${moduleId}/quiz/attempts`, token, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
}

// CHP-365/366: a trainee's saved (unsubmitted) quiz selections for the module's
// current version, as { question_id: option_id }. Lets the player rehydrate the
// quiz so a trainee resumes where they left off. Empty when there's no draft.
export function getTrainingQuizDraftAnswers(
  token: string,
  moduleId: string,
): Promise<Record<string, string>> {
  return request<Record<string, string>>(`/training/modules/${moduleId}/quiz/draft-answers`, token);
}

// CHP-365/366: persist one unsubmitted quiz selection (upsert keyed by
// question). Fire-and-forget from the player as the trainee picks an option;
// returns 204 (no body).
export function saveTrainingQuizDraftAnswer(
  token: string,
  moduleId: string,
  answer: TrainingQuizAnswerInput,
): Promise<void> {
  return request<void>(`/training/modules/${moduleId}/quiz/draft-answers`, token, {
    method: "POST",
    body: JSON.stringify(answer),
  });
}

export type TrainingUserModuleStatus = "completed" | "in_progress" | "not_started";

export type TrainingMeStatusItem = components["schemas"]["TrainingMeStatusItem"];

export function getTrainingMeStatus(token: string): Promise<TrainingMeStatusItem[]> {
  return request<TrainingMeStatusItem[]>("/training/me/status", token);
}

// RACCPE-25: manager-facing team training report. A Channel Manager sees the
// BDMs assigned to them; a Program Owner sees their own org's team (self +
// BDMs). `exempted` flags an active RACCPE-23 deal-block exemption — surfaced
// alongside the still-outstanding modules, not instead of them.
export type TrainingTeamReportItem = components["schemas"]["TrainingTeamReportItem"];

export function getTrainingTeamReport(token: string): Promise<TrainingTeamReportItem[]> {
  return request<TrainingTeamReportItem[]>("/training/reporting/team", token);
}

// RACCPE-26: RM/RA compliance export. The endpoint returns a CSV (not JSON), so
// this bypasses the JSON `request` helper and returns the raw blob plus the
// server-suggested filename from Content-Disposition (the caller turns it into a
// browser download). A plain <a href> can't carry the bearer token, hence fetch.
export async function downloadTrainingCompletionsExport(
  token: string,
): Promise<{ blob: Blob; filename: string }> {
  const resp = await fetch(`${baseUrl}/training/reporting/completions-export`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  const blob = await resp.blob();
  const disposition = resp.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? "training-completions.csv";
  return { blob, filename };
}

// ---------------------------------------------------------------------------
// Training authoring (RACCPE-3 / Slice 2) — RM/RA-gated mutations. No UI yet
// (Slice 3); these are the typed client stubs.
//
// The `*Admin` read types intentionally DO expose `is_correct` and
// `storage_key` (an author must see the answer key + asset binding). They are
// returned ONLY by the admin-gated endpoints below. The trainee types above
// (TrainingQuizOption / TrainingBlock) still omit those fields — keep them
// separate; never merge.
//
// Payload notes mirroring the backend schemas: `storage_key` is NOT settable
// (the Slice 3 upload pipeline owns it); `position` is server-assigned
// (append-only); `block_type` is immutable post-create (absent from the block
// update payload).
// ---------------------------------------------------------------------------

export type TrainingQuizOptionAdmin = components["schemas"]["TrainingQuizOptionAdminOut"];

export type TrainingQuizQuestionAdmin = components["schemas"]["TrainingQuizQuestionAdminOut"];

export type TrainingBlockAdmin = components["schemas"]["TrainingBlockAdminOut"];

export type TrainingModuleVersionAdmin = components["schemas"]["TrainingModuleVersionAdminOut"];

export type TrainingModuleAdmin = components["schemas"]["TrainingModuleAdminOut"];

export type TrainingModuleCreatePayload = components["schemas"]["TrainingModuleCreate"];

export type TrainingModuleUpdatePayload = components["schemas"]["TrainingModuleUpdate"];

export type TrainingBlockCreatePayload = components["schemas"]["TrainingBlockCreate"];

export type TrainingBlockUpdatePayload = components["schemas"]["TrainingBlockUpdate"];

export type TrainingQuizQuestionCreatePayload = components["schemas"]["TrainingQuizQuestionCreate"];

export type TrainingQuizQuestionUpdatePayload = components["schemas"]["TrainingQuizQuestionUpdate"];

export type TrainingQuizOptionCreatePayload = components["schemas"]["TrainingQuizOptionCreate"];

export type TrainingQuizOptionUpdatePayload = components["schemas"]["TrainingQuizOptionUpdate"];

export function listAdminTrainingModules(token: string): Promise<TrainingModuleListItem[]> {
  return request<TrainingModuleListItem[]>("/training/admin/modules", token);
}

/** CHP-539: module-scoped audit history, newest first — backs the editor's
 * Version history panel. Same require_training_author gate as the admin reads. */
export function getAdminTrainingModuleAudit(
  token: string,
  moduleId: string,
): Promise<AuditLogEntry[]> {
  return request<AuditLogEntry[]>(`/training/admin/modules/${moduleId}/audit`, token);
}

export function getAdminTrainingModule(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/admin/modules/${moduleId}`, token);
}

export function createTrainingModule(
  token: string,
  payload: TrainingModuleCreatePayload,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>("/training/modules", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateTrainingModule(
  token: string,
  moduleId: string,
  payload: TrainingModuleUpdatePayload,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function archiveTrainingModule(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}/archive`, token, {
    method: "POST",
  });
}

// RACCPE-6 / Slice 4: publish seals the draft's working version; republish
// seals a fresh copy-then-mutate clone of a published module. Both return the
// module with its (now sealed) version. A failed publishable precondition
// surfaces as a 422 with a `detail` string the UI shows verbatim.
export function publishTrainingModule(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}/publish`, token, {
    method: "POST",
  });
}

export function republishTrainingModule(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}/republish`, token, {
    method: "POST",
  });
}

// CHP-421: open an editable revision of a published module — clones the live
// version into an unpublished working version the editor can mutate while
// trainees keep seeing the live version. Edit → preview → republish to seal it.
export function reviseTrainingModule(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}/revise`, token, {
    method: "POST",
  });
}

// CHP-461 support: discard an in-progress revision — deletes the unpublished
// working version + its cloned content, leaving the live version untouched. The
// explicit cancel path so "republish with no changes" is no longer abused.
export function discardTrainingRevision(
  token: string,
  moduleId: string,
): Promise<TrainingModuleAdmin> {
  return request<TrainingModuleAdmin>(`/training/modules/${moduleId}/discard-revision`, token, {
    method: "POST",
  });
}

export function createTrainingBlock(
  token: string,
  moduleId: string,
  payload: TrainingBlockCreatePayload,
): Promise<TrainingBlockAdmin> {
  return request<TrainingBlockAdmin>(`/training/modules/${moduleId}/blocks`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateTrainingBlock(
  token: string,
  moduleId: string,
  blockId: string,
  payload: TrainingBlockUpdatePayload,
): Promise<TrainingBlockAdmin> {
  return request<TrainingBlockAdmin>(`/training/modules/${moduleId}/blocks/${blockId}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteTrainingBlock(
  token: string,
  moduleId: string,
  blockId: string,
): Promise<void> {
  return request<void>(`/training/modules/${moduleId}/blocks/${blockId}`, token, {
    method: "DELETE",
  });
}

export function createTrainingQuizQuestion(
  token: string,
  moduleId: string,
  payload: TrainingQuizQuestionCreatePayload,
): Promise<TrainingQuizQuestionAdmin> {
  return request<TrainingQuizQuestionAdmin>(`/training/modules/${moduleId}/quiz-questions`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateTrainingQuizQuestion(
  token: string,
  moduleId: string,
  questionId: string,
  payload: TrainingQuizQuestionUpdatePayload,
): Promise<TrainingQuizQuestionAdmin> {
  return request<TrainingQuizQuestionAdmin>(
    `/training/modules/${moduleId}/quiz-questions/${questionId}`,
    token,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export function deleteTrainingQuizQuestion(
  token: string,
  moduleId: string,
  questionId: string,
): Promise<void> {
  return request<void>(`/training/modules/${moduleId}/quiz-questions/${questionId}`, token, {
    method: "DELETE",
  });
}

export function createTrainingQuizOption(
  token: string,
  moduleId: string,
  questionId: string,
  payload: TrainingQuizOptionCreatePayload,
): Promise<TrainingQuizOptionAdmin> {
  return request<TrainingQuizOptionAdmin>(
    `/training/modules/${moduleId}/quiz-questions/${questionId}/options`,
    token,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function updateTrainingQuizOption(
  token: string,
  moduleId: string,
  questionId: string,
  optionId: string,
  payload: TrainingQuizOptionUpdatePayload,
): Promise<TrainingQuizOptionAdmin> {
  return request<TrainingQuizOptionAdmin>(
    `/training/modules/${moduleId}/quiz-questions/${questionId}/options/${optionId}`,
    token,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export function deleteTrainingQuizOption(
  token: string,
  moduleId: string,
  questionId: string,
  optionId: string,
): Promise<void> {
  return request<void>(
    `/training/modules/${moduleId}/quiz-questions/${questionId}/options/${optionId}`,
    token,
    { method: "DELETE" },
  );
}

// Training authoring — block media upload (CHP-1184: direct-to-MinIO).
// The browser no longer streams the file through the API. Instead: (1) ask the
// API for a short-lived presigned PUT URL, (2) PUT the bytes straight to MinIO
// (bypassing the API and the proxy body limit — the win for large videos),
// (3) call upload-complete so the API validates the stored object (size + magic
// bytes) and records it on the block. Only steps 1 and 3 carry the bearer token;
// the PUT is authorised by the presigned URL's signature.
//
// Content-Type is bound into the presigned URL, so the PUT must send exactly the
// same value we asked for. Browsers set `file.type` reliably for mp4/pdf/png/jpeg;
// we fall back by extension for the Office formats where it can be blank.
function guessTrainingContentType(file: File): string {
  // Lowercase so it matches the value the backend binds into the presigned
  // signature (it signs content_type.lower()); a case mismatch would 403 at MinIO.
  if (file.type) return file.type.toLowerCase();
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  const byExt: Record<string, string> = {
    mp4: "video/mp4",
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ppt: "application/vnd.ms-powerpoint",
  };
  return byExt[ext] ?? "application/octet-stream";
}

function putFileToPresignedUrl(
  url: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    // Must match the Content-Type bound into the presigned signature, or MinIO
    // rejects with SignatureDoesNotMatch. No Authorization header — the
    // signature in the URL authorises the write.
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new ApiError(`Upload to storage failed (${xhr.status})`, xhr.status, xhr.responseText),
      );
    };
    // A structural failure (network drop, CORS, aborted connection) — surface
    // it as an ApiError so the block shows the failure alert (CHP-809 #16).
    xhr.onerror = () => reject(new ApiError("Upload failed", 0, null));
    xhr.send(file);
  });
}

export async function uploadTrainingBlockAsset(
  token: string,
  moduleId: string,
  blockId: string,
  file: File,
  // CHP-809 (#16): optional determinate upload-progress callback (0..100),
  // reported off the direct-to-MinIO PUT (the large transfer). Without a
  // callback the progress step is simply skipped.
  onProgress?: (percent: number) => void,
): Promise<TrainingBlockAdmin> {
  const contentType = guessTrainingContentType(file);

  // 1. Presigned direct-to-MinIO PUT URL (validates block type + editable +
  //    content-type server-side before signing).
  const presign = await request<TrainingBlockUploadUrl>(
    `/training/modules/${moduleId}/blocks/${blockId}/upload-url`,
    token,
    { method: "POST", body: JSON.stringify({ content_type: contentType }) },
  );

  // Pre-reject an oversize file before spending the upload (the API re-checks
  // authoritatively at upload-complete).
  if (file.size > presign.max_bytes) {
    throw new ApiError(`file exceeds ${presign.max_bytes} byte limit`, 413, null);
  }

  // 2. Bytes straight to MinIO.
  await putFileToPresignedUrl(presign.url, file, contentType, onProgress);

  // 3. Finalise — the API validates the stored object and records it on the block.
  return request<TrainingBlockAdmin>(
    `/training/modules/${moduleId}/blocks/${blockId}/upload-complete`,
    token,
    { method: "POST", body: JSON.stringify({ original_filename: file.name }) },
  );
}

export function reorderTrainingBlocks(
  token: string,
  moduleId: string,
  order: string[],
): Promise<TrainingBlockAdmin[]> {
  return request<TrainingBlockAdmin[]>(`/training/modules/${moduleId}/blocks/order`, token, {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
}

export function reorderTrainingQuizQuestions(
  token: string,
  moduleId: string,
  order: string[],
): Promise<TrainingQuizQuestionAdmin[]> {
  return request<TrainingQuizQuestionAdmin[]>(
    `/training/modules/${moduleId}/quiz-questions/order`,
    token,
    { method: "PUT", body: JSON.stringify({ order }) },
  );
}

export function reorderTrainingQuizOptions(
  token: string,
  moduleId: string,
  questionId: string,
  order: string[],
): Promise<TrainingQuizOptionAdmin[]> {
  return request<TrainingQuizOptionAdmin[]>(
    `/training/modules/${moduleId}/quiz-questions/${questionId}/options/order`,
    token,
    { method: "PUT", body: JSON.stringify({ order }) },
  );
}

// ---------------------------------------------------------------------------
// Review overlay (comment-overlay build)
//
// In-app collaborative review surface. Gated on the backend `comments_enabled`
// flag (surfaced on /auth/me) AND a reviewer (staff) role — Channel Manager or
// ResetData Admin/Master. CHP-1287 (pen-test DR-4) tightened `require_reviewer`
// to staff-only, superseding ADR-0026's any-active-user soft-launch intent; the
// overlay mount now matches that gate (CHP-1300). Lets reviewers leave
// structured comments per live route and run "review cycles" that walk a
// reviewer through an ordered set of screens with per-screen sign-off.
//
// Every shape here MUST match the backend's Pydantic schemas under the
// `/review` prefix (the backend lane owns the server). Paths are exact —
// NO trailing slash (the FastAPI app runs redirect_slashes=False).
// ---------------------------------------------------------------------------

// CHP-509: base for Jira issue links built from a bare exported_ticket_ref
// (e.g. "CHP-456"). A plain string env override is safe (strings survive the
// docker entrypoint VITE substitution; only booleans tree-shake out), falling
// back to the org's cloud base. Synthetic dev keys (CHP-DEV-*) have no real
// issue and are NOT linked — see jiraBrowseUrl.
const JIRA_BASE_URL = (
  import.meta.env.VITE_JIRA_BASE_URL || "https://resetdata.atlassian.net"
).replace(/\/+$/, "");

/**
 * Browse URL for an exported Jira issue key, or null when the key is synthetic
 * (the local/dev Jira shim mints `CHP-DEV-…` keys that don't resolve to a real
 * issue, so they render as a plain, unlinked chip). Real keys → `<base>/browse/<key>`.
 */
export function jiraBrowseUrl(ticketRef: string): string | null {
  // Synthetic no-op keys are `<project>-DEV-<hex>` and project is operator-
  // configurable, so match project-key-agnostically (not just CHP-DEV-).
  if (/-DEV-[0-9A-F]+$/i.test(ticketRef)) return null;
  return `${JIRA_BASE_URL}/browse/${encodeURIComponent(ticketRef)}`;
}

export type ReviewSeverity = "blocker" | "change-needed" | "suggestion" | "question";
export type ReviewCategory = "copy" | "ux" | "flow" | "data" | "role-model" | "scope";
export type ReviewCommentStatus = "open" | "resolved";

export type ReviewCommentOut = components["schemas"]["ReviewCommentOut"];

export type JiraRef = components["schemas"]["JiraRef"];

// CHP-533: "cannot_check" is the third reviewer outcome — the screen couldn't
// be reached/exercised (blocked by a different bug/issue), as distinct from
// "not_complete" (reviewed but explicitly not finished). Neither counts as done.
export type SignoffOutcome = "done" | "not_complete" | "cannot_check";

export type ReviewScreenDetail = components["schemas"]["ReviewScreenDetail"];

export type ReviewCycleSummary = components["schemas"]["ReviewCycleSummary"];

export type ReviewCycleDetail = components["schemas"]["ReviewCycleDetail"];

export type ReviewCycleScreenInput = components["schemas"]["ReviewScreenCreate"];

export type ReviewCycleCreatePayload = components["schemas"]["ReviewCycleCreate"];

export type ReviewCommentCreatePayload = components["schemas"]["ReviewCommentCreate"];

export type ReviewCommentPatchPayload = components["schemas"]["ReviewCommentUpdate"];

export function listReviewCycles(
  token: string,
  params: { section?: string; status?: "open" | "closed" } = {},
): Promise<ReviewCycleSummary[]> {
  const qs = new URLSearchParams();
  if (params.section) qs.set("section", params.section);
  if (params.status) qs.set("status", params.status);
  const suffix = qs.toString();
  return request<ReviewCycleSummary[]>(`/review/cycles${suffix ? `?${suffix}` : ""}`, token);
}

export function getReviewCycle(token: string, cycleId: string): Promise<ReviewCycleDetail> {
  return request<ReviewCycleDetail>(`/review/cycles/${encodeURIComponent(cycleId)}`, token);
}

export function createReviewCycle(
  token: string,
  payload: ReviewCycleCreatePayload,
): Promise<ReviewCycleDetail> {
  return request<ReviewCycleDetail>("/review/cycles", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function closeReviewCycle(token: string, cycleId: string): Promise<ReviewCycleSummary> {
  return request<ReviewCycleSummary>(`/review/cycles/${encodeURIComponent(cycleId)}/close`, token, {
    method: "POST",
  });
}

// CHP-533: sign off a screen with an outcome (done | not_complete |
// cannot_check) and an optional free-text reason `note` (e.g. "blocked by
// CHP-123"). The note is only meaningful for the kick-back outcomes; passing it
// with `done` is harmless. Re-PUTting updates both outcome and note.
export function signoffScreen(
  token: string,
  screenId: string,
  outcome: SignoffOutcome = "done",
  note?: string | null,
): Promise<void> {
  return request<void>(`/review/screens/${encodeURIComponent(screenId)}/signoff`, token, {
    method: "PUT",
    body: JSON.stringify({ outcome, note: note ?? null }),
  });
}

export function unsignoffScreen(token: string, screenId: string): Promise<void> {
  return request<void>(`/review/screens/${encodeURIComponent(screenId)}/signoff`, token, {
    method: "DELETE",
  });
}

export function listReviewComments(
  token: string,
  params: { route_path?: string; cycle_id?: string; status?: ReviewCommentStatus } = {},
): Promise<ReviewCommentOut[]> {
  const qs = new URLSearchParams();
  if (params.route_path) qs.set("route_path", params.route_path);
  if (params.cycle_id) qs.set("cycle_id", params.cycle_id);
  if (params.status) qs.set("status", params.status);
  const suffix = qs.toString();
  return request<ReviewCommentOut[]>(`/review/comments${suffix ? `?${suffix}` : ""}`, token);
}

export function createReviewComment(
  token: string,
  payload: ReviewCommentCreatePayload,
): Promise<ReviewCommentOut> {
  return request<ReviewCommentOut>("/review/comments", token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchReviewComment(
  token: string,
  commentId: string,
  payload: ReviewCommentPatchPayload,
): Promise<ReviewCommentOut> {
  return request<ReviewCommentOut>(`/review/comments/${encodeURIComponent(commentId)}`, token, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteReviewComment(token: string, commentId: string): Promise<void> {
  return request<void>(`/review/comments/${encodeURIComponent(commentId)}`, token, {
    method: "DELETE",
  });
}

/**
 * Attach a PNG screenshot (the region the reviewer drew) to a comment. Sent as
 * multipart/form-data, so it bypasses the JSON `request` helper (which would set
 * a JSON content-type and break the multipart boundary).
 */
export async function uploadReviewScreenshot(
  token: string,
  commentId: string,
  blob: Blob,
): Promise<ReviewCommentOut> {
  const form = new FormData();
  form.append("file", blob, "region.png");
  const resp = await fetch(
    `${baseUrl}/review/comments/${encodeURIComponent(commentId)}/screenshot`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form },
  );
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  return (await resp.json()) as ReviewCommentOut;
}

/** Remove a comment's screenshot. Returns the updated comment (screenshot_url null). */
export function deleteReviewScreenshot(
  token: string,
  commentId: string,
): Promise<ReviewCommentOut> {
  return request<ReviewCommentOut>(
    `/review/comments/${encodeURIComponent(commentId)}/screenshot`,
    token,
    { method: "DELETE" },
  );
}

// ---- Help & Feedback (HF-04) ----------------------------------------------
// Fire-and-forget to JSM (ADR-0027): POST a feedback/support form, get back a
// tracking key. No GET endpoints. The category strings MUST match the backend
// `FeedbackCategory` enum (services/jsm.py) verbatim — they double as the
// dropdown labels and the submitted value, so the two can't drift.

export type FeedbackCategory = components["schemas"]["FeedbackCategory"];

/** Dropdown order — the 7 canonical hardcoded JSM request types (CHP-771;
 * "SPIF & Rewards" removed CHP-874). Mirrors `FeedbackCategory` in
 * services/jsm.py verbatim — the two cannot drift. */
export const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = [
  "Deal Registration",
  "Pricing & Margin",
  "Technical & Pre-Sales Support",
  "User Account Administration",
  "Report a Bug",
  "Feature Idea",
  "Other",
] as const;

export interface FeedbackCreatePayload {
  category: FeedbackCategory;
  /** "Summary" — 10–250 chars, required (backend-validated). */
  description: string;
  /** "Detailed Description" — optional. */
  additional_info?: string | null;
  /** Auto-captured from window.location.href; never user-entered. */
  current_url?: string | null;
  /**
   * "Response Preference" — true = "I'd like a follow-up", false = "No
   * feedback required". Defaults to true server-side if omitted.
   */
  wants_follow_up?: boolean;
}

export type FeedbackResponse = components["schemas"]["FeedbackResponse"];

/** Allowlist for the optional Help & Feedback attachment (CHP-813) — mirrors
 * the backend `feedback_attachment_allowed_content_types`. */
export const FEEDBACK_ATTACHMENT_ACCEPT = ["image/jpeg", "image/png", "application/pdf"] as const;
/** Max attachment size (5MB) — mirrors backend `feedback_attachment_max_bytes`. */
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export async function submitFeedback(
  token: string,
  payload: FeedbackCreatePayload,
  // CHP-813: optional single attachment (≤5MB, JPG/PNG/PDF). The endpoint is
  // multipart now, so the fields ride in FormData alongside the file.
  attachment?: File | null,
): Promise<FeedbackResponse> {
  const fd = new FormData();
  fd.append("category", payload.category);
  fd.append("description", payload.description);
  if (payload.additional_info != null) fd.append("additional_info", payload.additional_info);
  if (payload.current_url != null) fd.append("current_url", payload.current_url);
  if (payload.wants_follow_up != null) {
    fd.append("wants_follow_up", String(payload.wants_follow_up));
  }
  if (attachment) fd.append("attachment", attachment);

  // Post FormData directly rather than via `request`, which forces
  // Content-Type: application/json and would clobber the multipart boundary.
  const resp = await fetch(`${baseUrl}/feedback`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text().catch(() => null);
    }
    throw new ApiError(`API ${resp.status}`, resp.status, body);
  }
  return (await resp.json()) as FeedbackResponse;
}

// ---------------------------------------------------------------------------
// Program Insights (Wave 2) — program-wide staff analytics.
// ---------------------------------------------------------------------------
// Five read endpoints behind /insights/*, all gated require_manager_or_admin
// server-side and ALL program-wide (no per-role scope split — Wave-2 decision
// #1). The shapes below mirror apps/api/src/prm_api/schemas/insights.py exactly.
//
// MONETARY UNITS: every `*_value_cents` field is an integer in CENTS — the same
// unit the Deal model and DealDashboardMetrics use. Divide by 100 for display
// (the repo's formatAudCents helper does this), exactly like the existing
// dashboard scorecards. Never pre-divide on the wire.

/** Deals-tracking row of the Program Insights dashboard. Program-wide over
 * every non-draft deal. The "expiring" window is the Insights-specific 30-day
 * window (NOT the shared 14-day partner-dashboard EXPIRY_WINDOW). */
export type InsightsDealsRow = components["schemas"]["InsightsDealsRow"];

/** Growth-pulse row of the Program Insights dashboard. Counts are program-wide.
 * "new in 30d" = created_at within the trailing 30 days; "invited not
 * registered" = the display-status 'invited' cohort. */
export type InsightsGrowthRow = components["schemas"]["InsightsGrowthRow"];

/** GET /insights/dashboard — deals-tracking row + growth-pulse row in one call. */
export type InsightsDashboardMetrics = components["schemas"]["InsightsDashboardMetrics"];

/** One partner organisation in the deep-dive search list (GET /insights/partners).
 * Lightweight — NOT the heavyweight AdminUserListItemOut. */
export type InsightsPartnerListItem = components["schemas"]["InsightsPartnerListItem"];

/** One deal in the partner deep-dive Deals ledger. `value_cents` is cents
 * (divide by 100 for display); `owner_role` is "po" | "bdm" | null. */
export type InsightsPartnerDealRow = components["schemas"]["InsightsPartnerDealRow"];

/** One member in the partner deep-dive Training ledger. `cert_status` is the
 * three-state certification ("certified" | "unfinished" | "never_started"). */
export type InsightsPartnerMemberRow = components["schemas"]["InsightsPartnerMemberRow"];

/** GET /insights/partners/{id}/summary — per-org deep-dive aggregate:
 * profile/capacity + per-org deal metrics + per-org training summary, plus the
 * per-deal and per-member ledger rows that back the two sortable tables. */
export type InsightsPartnerSummary = components["schemas"]["InsightsPartnerSummary"];

/** One Channel-Partner organisation in the org-level Performance ledger
 * (GET /insights/partner-performance, CHP-782). One row per partner org;
 * mirrors schemas/insights.py InsightsPartnerPerformanceRow. `org_status` is
 * derived ('active' if any PO member has accepted the T&Cs, else 'invited').
 * `*_cents` fields are cents (divide by 100 for display); `certified_pct` is a
 * 0–100 percentage. `closed_won_fy_cents` is the current Australian financial
 * year to date (closed_at >= 1 July of the FY start), per the Flow spec. */
export type InsightsPartnerPerformanceRow = components["schemas"]["InsightsPartnerPerformanceRow"];

/** One row of the growth ledger — an org or an individual partner user.
 * `type` distinguishes the two; `status` is the display-status string for
 * individuals ('invited' / 'active' / 'pending' / …), or 'registered' for orgs. */
export type InsightsGrowthLedgerRow = components["schemas"]["InsightsGrowthLedgerRow"];

/** GET /insights/growth-ledger — rows plus the new-in-30d / invited-not-
 * registered segmentation counts. */
export type InsightsGrowthLedger = components["schemas"]["InsightsGrowthLedger"];

export function getInsightsDashboard(token: string): Promise<InsightsDashboardMetrics> {
  return request<InsightsDashboardMetrics>("/insights/dashboard", token);
}

/** Search partner organisations for the deep-dive picker. `q` is a
 * case-insensitive substring match on company_name (omit to list all). */
export function searchInsightsPartners(
  token: string,
  q?: string,
): Promise<InsightsPartnerListItem[]> {
  const qs = q && q.trim() !== "" ? `?q=${encodeURIComponent(q.trim())}` : "";
  return request<InsightsPartnerListItem[]>(`/insights/partners${qs}`, token);
}

/** GET /insights/partner-performance — the org-level Channel-Partner
 * Performance ledger (one row per partner org). `q` is a case-insensitive
 * substring match on company_name (omit to list all). */
export function getPartnerPerformance(
  token: string,
  q?: string,
): Promise<InsightsPartnerPerformanceRow[]> {
  const qs = q && q.trim() !== "" ? `?q=${encodeURIComponent(q.trim())}` : "";
  return request<InsightsPartnerPerformanceRow[]>(`/insights/partner-performance${qs}`, token);
}

export function getInsightsPartnerSummary(
  token: string,
  partnerId: string,
): Promise<InsightsPartnerSummary> {
  return request<InsightsPartnerSummary>(
    `/insights/partners/${encodeURIComponent(partnerId)}/summary`,
    token,
  );
}

/** The Insights period-toggle vocabulary. "all" omits the param (no window);
 * the rest map to current calendar month / quarter / year on the backend. */
export type InsightsPeriod = "all" | "month" | "quarter" | "year";

/** Program-wide growth ledger. `q` searches across entity name AND
 * partner/company name; `status` filters individuals to a display-status
 * ('invited' | 'active' | 'pending' | …) or 'registered'; `period` restricts
 * rows to the entity created_at window (month|quarter|year). */
export function getInsightsGrowthLedger(
  token: string,
  opts?: { q?: string; status?: string; period?: InsightsPeriod },
): Promise<InsightsGrowthLedger> {
  const params = new URLSearchParams();
  if (opts?.q && opts.q.trim() !== "") params.set("q", opts.q.trim());
  if (opts?.status && opts.status !== "all") params.set("status", opts.status);
  if (opts?.period && opts.period !== "all") params.set("period", opts.period);
  const qs = params.toString();
  return request<InsightsGrowthLedger>(`/insights/growth-ledger${qs ? `?${qs}` : ""}`, token);
}

export { ApiError };
